/**
 * Currency, and the one thing the rest of the app must never get wrong about
 * it: how many of a currency's smallest units make up the amounts we store.
 *
 * Everything in this codebase stores and passes money as an integer number of
 * **hundredths of a major unit** — "cents" in every field name, whatever the
 * currency. That single convention is what lets `formatCents`, the pricing
 * maths and every `*_cents` column stay currency-agnostic: `Intl` renders
 * `cents / 100` with the right number of decimals for the currency, so 150000
 * is ¥1,500 in JPY and $1,500.00 in USD without any of the callers caring.
 *
 * Payment providers do NOT use that convention. They take amounts in the
 * currency's own smallest unit — whole yen for JPY, thousandths of a dinar for
 * KWD — so a stored amount has to be converted on the way out. Passing 150000
 * to Stripe for a ¥1,500 charge would take ¥150,000 off the customer, a 100x
 * overcharge, which is why that conversion lives here and is applied in one
 * place at the provider boundary rather than being open-coded per call site.
 */

/** How a currency behaves once real money is involved. */
export interface CurrencyInfo {
  /** Decimal places the currency itself has: 0 (JPY), 2 (USD), 3 (KWD). */
  digits: 0 | 2 | 3;
  /**
   * Amounts must land on a whole major unit. Always true for a zero-decimal
   * currency (there is nothing smaller to charge), and also true for the
   * handful of two-decimal currencies whose providers only accept amounts
   * divisible by 100.
   */
  wholeUnitsOnly?: true;
}

/**
 * The currencies a company may trade in, with the properties that change how
 * an amount is charged. Two decimal places is by far the common case, so the
 * table below is grouped by exception: everything not named in the
 * zero-decimal, three-decimal or whole-unit groups is a plain 2.
 *
 * The list is the set Stripe accepts as a presentment currency. A company can
 * only pick from it, because a currency the app cannot convert amounts for is
 * a currency it cannot safely charge in.
 */

/** No sub-unit at all: the provider's smallest unit is the major unit. */
const ZERO_DECIMAL = [
  'BIF',
  'CLP',
  'DJF',
  'GNF',
  'JPY',
  'KMF',
  'KRW',
  'MGA',
  'PYG',
  'RWF',
  'UGX',
  'VND',
  'VUV',
  'XAF',
  'XOF',
  'XPF',
] as const;

/** Three decimal places; providers take amounts in thousandths. */
const THREE_DECIMAL = ['BHD', 'JOD', 'KWD', 'OMR', 'TND'] as const;

/**
 * Two decimal places on paper, but the provider only accepts amounts that are
 * a whole number of major units, so a charge of 1234 (12.34) is refused.
 */
const WHOLE_UNIT_TWO_DECIMAL = ['HUF', 'ISK', 'TWD'] as const;

/**
 * Currencies this app will not let a company choose, despite knowing how to
 * convert them.
 *
 * These four sit in the part of Stripe's documentation that has changed over
 * the years: whether each is zero-decimal, or two-decimal-but-divisible-by-100
 * for backwards compatibility, is genuinely hard to pin down, and the two
 * readings differ by a factor of 100 in what reaches the customer's card. UGX
 * is the sharpest case — it appears in the zero-decimal list and in the
 * special-case note — but the same doubt covers all four.
 *
 * A guess that is wrong in one direction charges 100x; wrong in the other, the
 * charge is refused at the till. Neither is an acceptable thing to ship on a
 * coin flip, so they are simply not offered. They keep their entries in the
 * table above so that any row already carrying one still renders and
 * reconciles — it just cannot be selected, and cannot be charged in.
 *
 * To re-enable one: confirm its representation against Stripe's current
 * "Zero-decimal currencies" and "Special cases" documentation, put it in the
 * group that matches, and delete it from here.
 */
const UNVERIFIED_REPRESENTATION = ['HUF', 'ISK', 'TWD', 'UGX'] as const;

/** Ordinary two-decimal currencies. */
const TWO_DECIMAL = [
  'AED',
  'AFN',
  'ALL',
  'AMD',
  'ANG',
  'AOA',
  'ARS',
  'AUD',
  'AWG',
  'AZN',
  'BAM',
  'BBD',
  'BDT',
  'BGN',
  'BMD',
  'BND',
  'BOB',
  'BRL',
  'BSD',
  'BWP',
  'BYN',
  'BZD',
  'CAD',
  'CDF',
  'CHF',
  'CNY',
  'COP',
  'CRC',
  'CVE',
  'CZK',
  'DKK',
  'DOP',
  'DZD',
  'EGP',
  'ETB',
  'EUR',
  'FJD',
  'FKP',
  'GBP',
  'GEL',
  'GIP',
  'GMD',
  'GTQ',
  'GYD',
  'HKD',
  'HNL',
  'HTG',
  'IDR',
  'ILS',
  'INR',
  'JMD',
  'KES',
  'KGS',
  'KHR',
  'KYD',
  'KZT',
  'LAK',
  'LBP',
  'LKR',
  'LRD',
  'LSL',
  'MAD',
  'MDL',
  'MKD',
  'MMK',
  'MNT',
  'MOP',
  'MUR',
  'MVR',
  'MWK',
  'MXN',
  'MYR',
  'MZN',
  'NAD',
  'NGN',
  'NIO',
  'NOK',
  'NPR',
  'NZD',
  'PAB',
  'PEN',
  'PGK',
  'PHP',
  'PKR',
  'PLN',
  'QAR',
  'RON',
  'RSD',
  'SAR',
  'SBD',
  'SCR',
  'SEK',
  'SGD',
  'SHP',
  'SLE',
  'SOS',
  'SRD',
  'SZL',
  'THB',
  'TJS',
  'TOP',
  'TRY',
  'TTD',
  'TZS',
  'UAH',
  'USD',
  'UYU',
  'UZS',
  'WST',
  'XCD',
  'YER',
  'ZAR',
  'ZMW',
] as const;

export const CURRENCIES: Readonly<Record<string, CurrencyInfo>> = Object.freeze({
  ...Object.fromEntries(TWO_DECIMAL.map((code) => [code, { digits: 2 } as CurrencyInfo])),
  ...Object.fromEntries(ZERO_DECIMAL.map((code) => [code, { digits: 0, wholeUnitsOnly: true } as CurrencyInfo])),
  ...Object.fromEntries(THREE_DECIMAL.map((code) => [code, { digits: 3 } as CurrencyInfo])),
  ...Object.fromEntries(
    WHOLE_UNIT_TWO_DECIMAL.map((code) => [code, { digits: 2, wholeUnitsOnly: true } as CurrencyInfo]),
  ),
});

/** Every selectable code, alphabetically — the order the pickers render in. */
export const SUPPORTED_CURRENCY_CODES: readonly string[] = Object.freeze(
  Object.keys(CURRENCIES)
    .filter((code) => !(UNVERIFIED_REPRESENTATION as readonly string[]).includes(code))
    .sort(),
);

/** What a company gets until it picks something else. */
export const DEFAULT_CURRENCY_CODE = 'USD';

export function normalizeCurrencyCode(code: string): string {
  return code.trim().toUpperCase();
}

/**
 * Whether a company may trade in this currency — which is narrower than
 * whether the table knows about it. A currency whose provider representation
 * is unverified converts correctly for anything already stored but is refused
 * for anything new, at selection *and* at the point of charging.
 */
export function isSupportedCurrency(code: string): boolean {
  const normalized = normalizeCurrencyCode(code);
  return normalized in CURRENCIES && !(UNVERIFIED_REPRESENTATION as readonly string[]).includes(normalized);
}

/**
 * The currency's own properties, falling back to the ordinary two-decimal
 * shape for a code this table has never heard of. Unknown codes cannot be
 * *chosen* (the schemas reject them), but a row written before a code left the
 * list must still render and reconcile rather than throw.
 */
export function currencyInfo(code: string): CurrencyInfo {
  return CURRENCIES[normalizeCurrencyCode(code)] ?? { digits: 2 };
}

/** Decimal places to show — what `Intl` would use for this currency. */
export function currencyDigits(code: string): 0 | 2 | 3 {
  return currencyInfo(code).digits;
}

/**
 * Decimal places an amount can actually be *entered* with, which is not always
 * what the currency has.
 *
 * Amounts are stored as hundredths of a major unit everywhere in this app, so
 * a three-decimal currency is stored one decimal place coarser than it is
 * denominated: 12.345 KWD cannot be held, only 12.34 or 12.35 (which is
 * 12.340 or 12.350 dinars — both exact, and both what the customer is charged).
 * Offering a third box to type in would promise a precision the store cannot
 * keep and silently round it away, so entry stops at two places and prices in
 * these currencies are set to the nearest 10 fils.
 */
export function currencyInputDigits(code: string): 0 | 2 {
  return currencyDigits(code) === 0 ? 0 : 2;
}

/**
 * Half away from zero, matching `roundHalfUp` in the pricing engine.
 *
 * Duplicated rather than imported because the dependency runs the other way —
 * pricing reads this module. It matters for the same reason it does there:
 * fees may be negative (a credit line), and `Math.round` rounds -12.5 to -12
 * while rounding +12.5 to +13, so a credit and a charge of the same size would
 * not mirror each other.
 */
function roundHalfAwayFromZero(value: number): number {
  return value < 0 ? -Math.round(-value) : Math.round(value);
}

/**
 * Converts a stored amount (hundredths of a major unit) into the smallest unit
 * the payment provider bills in.
 *
 * Exact by construction for two- and three-decimal currencies. For a
 * zero-decimal currency it is exact only when the amount is a whole major
 * unit, which `isChargeableAmount` is there to guarantee before any money
 * moves; the rounding here is a last line of defense, not the contract.
 */
export function toProviderMinorUnits(cents: number, code: string): number {
  const { digits } = currencyInfo(code);
  if (digits === 0) return roundHalfAwayFromZero(cents / 100);
  if (digits === 3) return cents * 10;
  return cents;
}

/** The inverse, for reading provider amounts back into stored cents. */
export function fromProviderMinorUnits(amount: number, code: string): number {
  const { digits } = currencyInfo(code);
  if (digits === 0) return amount * 100;
  if (digits === 3) return roundHalfAwayFromZero(amount / 10);
  return amount;
}

/** The smallest step this currency can express, in stored cents. */
export function currencyStepCents(code: string): number {
  return currencyInfo(code).wholeUnitsOnly ? 100 : 1;
}

/** Rounds a stored amount onto a value the currency can actually charge. */
export function roundToCurrencyStep(cents: number, code: string): number {
  const step = currencyStepCents(code);
  return roundHalfAwayFromZero(cents / step) * step;
}

/**
 * The smallest charge worth attempting, in stored cents.
 *
 * Card networks refuse trivially small amounts — half a major unit is the
 * long-standing floor — and a currency billed in whole units cannot go below
 * one of them. The provider may enforce a higher minimum of its own; that
 * comes back as a declined charge with the provider's reason on it.
 */
export function minimumChargeCents(code: string): number {
  return Math.max(50, currencyStepCents(code));
}

/**
 * Whether an amount can be charged as-is: at or above the floor, and landing
 * on a unit the currency can express. Checked before a ledger row is written,
 * so an unchargeable amount is refused with an explanation rather than
 * silently rounded into a different number than the one that was approved.
 */
export function isChargeableAmount(cents: number, code: string): boolean {
  if (!Number.isInteger(cents) || cents < minimumChargeCents(code)) return false;
  return cents % currencyStepCents(code) === 0;
}

/**
 * Zero at the precision an amount field accepts, for that field's placeholder.
 * Uses the entry precision rather than the currency's own, so the placeholder
 * cannot promise a decimal place the field will not keep.
 */
export function formatEntryPlaceholder(code: string, locale = 'en-US'): string {
  const digits = currencyInputDigits(code);
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: normalizeCurrencyCode(code),
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
      numberingSystem: 'latn',
    }).format(0);
  } catch {
    return (0).toFixed(digits);
  }
}

/**
 * An amount written out in full, including the part its currency has no unit
 * for. Only for explaining *why* an amount cannot be charged: the ordinary
 * `formatCents` renders ¥500.50 as "¥500" — correct for a price tag, useless
 * in a message whose whole point is the half yen, where it would read
 * "¥500 cannot be charged; charge ¥500 instead".
 */
export function formatExactAmount(cents: number, code: string, locale = 'en-US'): string {
  const digits = Math.max(2, currencyDigits(code));
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: normalizeCurrencyCode(code),
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(digits)} ${code}`;
  }
}
